import type { EffectiveClass } from './effectiveClass';

export interface SessionParticipantNames {
  clientName?: string | null;
  practitionerName?: string | null;
  /** Coarse practitioner role surfaced by the BE on session rows. Mirrors
   *  `sessionService.ts` in elixaa-core — `chief_psychologist` collapses to
   *  `psychologist` so org hierarchy isn't leaked to clients. Drives the
   *  nameplate's role label: 'psychologist' → "Psychologist",
   *  'volunteer' → "Volunteer". Falls back to LiveKit identity prettify
   *  (then "Practitioner") on legacy sessions that don't carry it. */
  practitionerKind?: 'psychologist' | 'volunteer' | null;
}

export interface ParticipantLike {
  identity: string;
  name?: string;
  isLocal?: boolean;
}

export interface ParticipantDisplay {
  /** Human-readable full name (e.g. "Magesh Sundar"). Falls back to role label. */
  name: string;
  /** Role label in sentence case (e.g. "Chief Psychologist", "Client"). */
  role: string;
}

/**
 * Telehealth sessions are 1:1 — one client, one practitioner — so the remote
 * participant's role is always the opposite of the viewer's. We combine that
 * with `clientName` / `practitionerName` from the session endpoint to produce
 * a proper nameplate, independent of what LiveKit identity the backend happens
 * to mint (userId hash, role string, or anything else).
 *
 * Observer viewers see both sides — for them we fall back to `participant.name`
 * / identity hints to decide which session name to surface.
 */
export function displayForParticipant(
  participant: ParticipantLike,
  viewerClass: EffectiveClass,
  sessionNames: SessionParticipantNames,
): ParticipantDisplay {
  const role = resolveParticipantClass(participant, viewerClass);
  const sessionName = role === 'client' ? sessionNames.clientName : sessionNames.practitionerName;
  const roleLabel = roleLabelForClass(role, participant.name, sessionNames.practitionerKind);
  const trimmedName = trimOrUndefined(sessionName);
  return {
    name: trimmedName ?? roleLabel,
    role: roleLabel,
  };
}

function resolveParticipantClass(
  participant: ParticipantLike,
  viewerClass: EffectiveClass,
): EffectiveClass {
  if (participant.isLocal === true) return viewerClass;
  // 1:1 session — the remote is the opposite class.
  if (viewerClass === 'client') return 'practitioner';
  if (viewerClass === 'practitioner') return 'client';
  // Observer viewer — infer from backend-set name / identity.
  const hint = `${participant.name ?? ''} ${participant.identity}`.toLowerCase();
  return hint.includes('client') ? 'client' : 'practitioner';
}

function roleLabelForClass(
  cls: EffectiveClass,
  backendNameHint: string | undefined,
  practitionerKind: SessionParticipantNames['practitionerKind'],
): string {
  if (cls === 'client') return 'Client';
  if (cls === 'observer') return 'Observer';
  // Practitioner. Prefer the session row's coarse `practitionerKind` since
  // it's the authoritative signal: it's what the BE deliberately exposes,
  // it survives opaque LiveKit identities, and it already collapses
  // chief_psychologist → psychologist (no hierarchy leak to clients).
  if (practitionerKind === 'volunteer') return 'Volunteer';
  if (practitionerKind === 'psychologist') return 'Psychologist';
  // Fallback path for legacy sessions without practitionerKind: prettify
  // the backend-provided identity hint if it's a human string, otherwise
  // settle for the generic role label.
  if (backendNameHint && !looksLikeOpaqueId(backendNameHint)) {
    return prettifyIdentity(backendNameHint);
  }
  return 'Practitioner';
}

function looksLikeOpaqueId(s: string): boolean {
  // Backend userId pattern: "US" + 24+ hex chars. Also catches bare UUID-ish.
  return /^us[0-9a-f]{8,}$/i.test(s) || /^[0-9a-f-]{16,}$/i.test(s);
}

export function prettifyIdentity(identity: string): string {
  if (!identity) return 'Participant';
  if (identity.includes(' ')) return identity;
  return identity
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.charAt(0) ?? '?').toUpperCase();
  return ((parts[0]?.charAt(0) ?? '') + (parts[parts.length - 1]?.charAt(0) ?? '')).toUpperCase();
}

function trimOrUndefined(v: string | null | undefined): string | undefined {
  if (v == null) return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
